import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { FunctionsHttpError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { useOverlay } from '@/components/Overlay';
import type { Chat, ChatLabel, ChatMessage, CoachPostType, Delivery, Inbox, LeadDetails, LeadGrade, MessageKind, TeamChat, TeamPerson, ThreadItem, Tier } from './types';
import { loadPeople } from '@/lib/people';
import { isOpen } from '@/lib/hours';
import { StoreContext, readKeepArchived, type ChatsStore, type OutgoingMessage } from './store';
import { playChime } from '@/lib/sound';
import { useAuth } from '@/lib/auth';
import { autoArchived } from '@/lib/chatGroups';
import { systemLine } from '@/lib/teamInfo';
import { isLeadCard } from '@/lib/leadCard';
import { ASSISTANT_NAMES, type AiTeamMember } from '@/lib/assistant';

// Live chats from Sentinel, for everyone signed in (see ChatsProvider in store.tsx).
//
// Reads with the person's own login, so the database's row rules decide what they
// see: head office every chat, the POS team POS chats, an operator only their own.
// Every change to a customer chat (reply, read ticks, note, take over) goes through
// the messaging-send edge function; only the person's own archive, pin and mute
// are written here directly. Live updates come from Supabase realtime.
// Tables: supabase/migrations/20261014b_messaging_inbox.sql.

interface ConversationRow {
  id: string;
  channel: 'whatsapp' | 'email' | 'facebook' | 'instagram';
  brand: 'cmb' | 'pos';
  /** Empty for a website enquiry with no mobile (an email lead, Gary 17 Oct). */
  identity_id: string | null;
  territory_id: string | null;
  assigned_user_id: string | null;
  inbox: 'leads' | 'billing' | 'customer_service';
  status: 'unassigned' | 'waiting_operator' | 'waiting_customer' | 'booked' | 'closed';
  qualifier_step: string | null;
  qualifier: Record<string, string> | null;
  last_inbound_at: string | null;
  first_reply_at: string | null;
  reply_due_at: string | null;
  escalated_at: string | null;
  updated_at: string;
  customer_id: string | null;
  lead_id: string | null;
  ai_state: 'off' | 'ai' | 'human' | 'human_required';
  ai_handover_reason: string | null;
  busy_remind_at: string | null;
  source: Record<string, unknown> | null;
  /** Leo's grade for the enquiry (Gary, 28 Oct): on the chat, so an enquiry without a suburb is graded too. */
  grade: LeadGrade | null;
}

interface MessageRow {
  id: string;
  conversation_id: string;
  direction: 'in' | 'out' | 'internal';
  sender: 'customer' | 'operator' | 'auto' | 'system' | 'note' | 'assistant';
  kind: string;
  body: string | null;
  media_path: string | null;
  reply_to_message_id: string | null;
  reaction: string | null;
  our_reaction: string | null;
  sign_off: string | null;
  delivery_status: string;
  error: string | null;
  sent_by_user_id: string | null;
  created_at: string;
  /** Meta's id; for a website enquiry's lead card, lead-card:<lead id>. */
  provider_message_id?: string | null;
  /** Email chats: subject and threading (Gary, 19 Oct). */
  email_meta?: { subject?: string | null; from?: string | null } | null;
}

interface IdentityRow { id: string; display_name: string | null; phone_e164: string | null; external_id: string }
interface StateRow { conversation_id: string; last_read_at: string | null; archived_at: string | null; pinned_at: string | null; muted_until: string | null; marked_unread_at: string | null }
interface NameRow { id: string; first_name: string | null; last_name: string | null }

/** A message's reactions grouped by emoji, most given first, with who gave each ("You" first). Undefined when none. */
function reactionGroups(list: { user_id: string; emoji: string }[], me: string, nameOf: Map<string, string>): { emoji: string; names: string[] }[] | undefined {
  if (!list.length) return undefined;
  const by = new Map<string, string[]>();
  for (const r of list) by.set(r.emoji, [...(by.get(r.emoji) ?? []), r.user_id === me ? 'You' : nameOf.get(r.user_id) ?? 'Colleague']);
  return [...by.entries()].map(([emoji, names]) => ({ emoji, names: names.sort((a, b) => (a === 'You' ? -1 : b === 'You' ? 1 : a.localeCompare(b))) }))
    .sort((a, b) => b.names.length - a.names.length);
}

function fullName(r: NameRow | undefined): string {
  return r ? [r.first_name, r.last_name].map((p) => p?.trim()).filter(Boolean).join(' ') : '';
}

interface Snapshot {
  conversations: ConversationRow[];
  messages: MessageRow[];
  identities: Map<string, IdentityRow>;
  territories: Map<string, string>;
  people: Map<string, string>;
  /** Names from the linked customer and lead records, for contacts whose WhatsApp shares no name. */
  customerNames: Map<string, string>;
  leadNames: Map<string, string>;
  states: Map<string, StateRow>;
  media: Map<string, string>;
  /** Active shared labels, and which chats carry which. */
  labels: ChatLabel[];
  chatLabels: Map<string, string[]>;
  /** This person's own marks on messages: starred, and hidden (Delete for me). */
  marks: Map<string, { starred: boolean; hidden: boolean }>;
  /** Customers with an invoice sent or overdue and money owing (row rules apply). */
  unpaid: Set<string>;
  /** Customers who paid an invoice in the last 30 days (card stage Paid, Gary 18 Oct). */
  paid: Set<string>;
  /** Each customer's loyalty tier (status chip, Gary 18 Oct). */
  customerTiers: Map<string, Tier>;
  /** Customers and leads with Leo off (Gary, 20 Oct), as "c:<id>" and "l:<id>". */
  leoOff: Set<string>;
  /** People with Leo on duty for their chats (users.chats_away_at), and public holidays (YYYY-MM-DD). */
  away: Set<string>;
  holidays: Set<string>;
}

// Marks a head office inbox chat (Accounts, Customer service), where the "Add my name" choice shows.
const SIGN_OFF: Record<'cmb' | 'pos', Record<'billing' | 'customer_service', string>> = {
  cmb: { billing: 'CleanMyBed - Accounts', customer_service: 'CleanMyBed - Customer Service' },
  pos: { billing: 'Peace of Sleep - Accounts', customer_service: 'Peace of Sleep - Customer Service' },
};

const INBOX: Record<ConversationRow['inbox'], Inbox | undefined> = { leads: undefined, billing: 'accounts', customer_service: 'service' };
const DAY_MS = 24 * 60 * 60 * 1000;
const MEDIA_TTL_S = 600;
const SOON = 'Coming soon on live chats';

function hhmm(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function dayLabel(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return 'Today';
  if (sameDay(d, new Date(now.getTime() - DAY_MS))) return 'Yesterday';
  return d.toLocaleDateString('en-GB', { weekday: 'long', day: 'numeric', month: 'long' });
}

/** Ted's coaching post titles (Gary, 2 Nov 2026), shown with the date, e.g. "Monday standup · Mon 2 Nov". */
const COACH_POST_NAMES: Record<CoachPostType, string> = {
  weekly_review: 'Weekly review',
  monday_standup: 'Monday standup',
  ideas: 'Ideas',
  daily_check: 'Daily check',
  midweek_check: 'Mid-week check',
  nudge: 'Afternoon nudge',
};

function shortDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-GB', { weekday: 'short', day: 'numeric', month: 'short' });
}

/** The list's time column: the time today, the weekday this week, else the date. */
function listTime(iso: string): string {
  const d = new Date(iso);
  const now = new Date();
  if (sameDay(d, now)) return hhmm(iso);
  if (now.getTime() - d.getTime() < 6 * DAY_MS) return d.toLocaleDateString('en-GB', { weekday: 'short' });
  return d.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

function colourOf(id: string): number {
  let h = 0;
  for (const ch of id) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return h % 6;
}

function deliveryOf(status: string): Delivery {
  return status === 'sent' || status === 'delivered' || status === 'read' || status === 'failed' ? status : 'pending';
}

/**
 * Approved messages are stored with their buttons as a last line, "[Pay securely: https://...]
 * [I have a question] [I've already paid]" (messaging-send). The customer sees buttons, so the
 * thread shows the text without that line and the buttons by name only.
 */
export function splitButtons(body: string): { text: string; buttons: string[] } {
  const lines = body.replace(/\s+$/, '').split('\n');
  const last = lines[lines.length - 1] ?? '';
  if (!/^(\[[^\]]+\]\s*)+$/.test(last.trim())) return { text: body, buttons: [] };
  const buttons = [...last.matchAll(/\[([^\]]+)\]/g)].map((b) => (b[1] ?? '').split(':')[0]!.trim()).filter(Boolean);
  return { text: lines.slice(0, -1).join('\n').replace(/\s+$/, ''), buttons };
}

/**
 * A message sent with a link button (a quick link, or Dr C's "See our prices") is stored with
 * "[label] url" as its last line. The customer sees the text and a button, so the thread does too.
 */
export function splitLinkButton(body: string): { text: string; button: string | null } {
  const m = /\n\n\[([^\]]{1,20})\] (https?:\/\/\S+)\s*$/.exec(body);
  return m ? { text: body.slice(0, m.index).replace(/\s+$/, ''), button: m[1]! } : { text: body, button: null };
}

// A sent or received file shows as its name and type, never its temporary download link (Gary, 3 Nov 2026).
function fileLabel(kind: string, mime: string | null, name: string | null | undefined): string {
  if (kind === 'video') return 'Video';
  if (mime === 'application/pdf' || /\.pdf$/i.test(name ?? '')) return 'PDF';
  return 'Document';
}

function bodyOf(m: MessageRow, media: Map<string, string>): MessageKind {
  // Dr C's pictures come from our public Content Library: their link is the path itself.
  const url = m.media_path ? (m.media_path.startsWith('https://') ? m.media_path : media.get(m.media_path)) : undefined;
  switch (m.kind) {
    case 'image': return { kind: 'photo', caption: m.body ?? '', url };
    case 'audio': return { kind: 'voice', duration: '', url };
    case 'template': return { kind: 'text', text: splitButtons(m.body ?? '').text };
    case 'interactive': return m.direction === 'out' ? { kind: 'text', text: splitLinkButton(m.body ?? '').text } : { kind: 'text', text: m.body ?? '' };
    case 'video':
    case 'document':
      return url ? { kind: 'link', title: m.body || (m.kind === 'video' ? 'Video' : 'Document'), url, text: '', file: fileLabel(m.kind, null, m.body) } : { kind: 'text', text: m.body || 'Attachment' };
    default: return { kind: 'text', text: m.body ?? '' };
  }
}

function leadOf(q: Record<string, string> | null): LeadDetails[] | undefined {
  if (!q) return undefined;
  const rows: LeadDetails[] = [];
  if (q.suburb) rows.push({ label: 'Suburb', value: q.suburb });
  if (q.mattresses) rows.push({ label: 'Mattresses', value: q.mattresses });
  if (q.date) rows.push({ label: 'Preferred day', value: q.date });
  return rows.length ? rows : undefined;
}

/** A divider line in the thread, shown with the existing system-line style. */
function divider(id: string, conversationId: string, text: string, at: string): MessageRow {
  return {
    id, conversation_id: conversationId, direction: 'internal', sender: 'system', kind: 'system', body: text,
    media_path: null, reply_to_message_id: null, reaction: null, our_reaction: null, sign_off: null, delivery_status: 'delivered',
    error: null, sent_by_user_id: null, created_at: at,
  };
}

/**
 * The customer's earlier chats with us (same contact, same brand), oldest first, each behind
 * an "Earlier chat" line, so a new chat reads as one continuous WhatsApp thread (Gary, 14 Oct).
 */
function earlierRows(c: ConversationRow, all: ConversationRow[], byConversation: Map<string, MessageRow[]>): MessageRow[] {
  const own = byConversation.get(c.id) ?? [];
  const start = own[0]?.created_at;
  // An email lead has no contact to share earlier chats with.
  if (!start || !c.identity_id) return [];
  const earlier = all
    .filter((o) => o.id !== c.id && o.identity_id === c.identity_id && o.brand === c.brand)
    .map((o) => ({ o, rows: byConversation.get(o.id) ?? [] }))
    .map(({ o, rows }) => ({ o, rows, first: rows[0]?.created_at ?? '' }))
    .filter(({ first }) => first !== '' && first < start)
    .sort((a, b) => (a.first < b.first ? -1 : 1));
  if (!earlier.length) return [];
  const out: MessageRow[] = [];
  for (const { o, rows, first } of earlier) {
    out.push(divider(`earlier-${o.id}`, c.id, 'Earlier chat', first), ...rows);
  }
  out.push(divider(`current-${c.id}`, c.id, 'This chat', start));
  return out;
}

/**
 * One row per person, as in WhatsApp (Gary, 15 Oct): for each contact and brand, the open chat
 * (else the latest), with the person's earlier chats folded into its thread (earlierRows).
 * Conversations arrive newest first.
 */
function onePerPerson(conversations: ConversationRow[]): ConversationRow[] {
  const pick = new Map<string, ConversationRow>();
  for (const c of conversations) {
    const key = `${c.identity_id ?? c.id}|${c.brand}`;
    const held = pick.get(key);
    if (!held || (held.status === 'closed' && c.status !== 'closed')) pick.set(key, c);
  }
  const keep = new Set([...pick.values()].map((c) => c.id));
  return conversations.filter((c) => keep.has(c.id));
}

/**
 * Who answers this chat right now (Gary, 21 Oct: head office must see at once who owns a chat and who is on
 * duty). Leo while he has it; on a person's chat while they have Leo on duty, outside opening hours, or on cover
 * after 20 minutes (the rule the server follows, _shared/drc/engine.ts skipReason). Otherwise the owner. Never
 * Leo on email, Peace of Sleep, a customer with Leo off, or a chat a person switched him off on.
 */
function onDutyOf(c: ConversationRow, snap: Snapshot, rows: MessageRow[], email: boolean, now: number): 'leo' | 'owner' {
  const leoOff = (!!c.customer_id && snap.leoOff.has(`c:${c.customer_id}`)) || (!!c.lead_id && snap.leoOff.has(`l:${c.lead_id}`));
  if (c.brand !== 'cmb' || email || leoOff) return 'owner';
  if (c.ai_state === 'ai') return 'leo';
  if (c.ai_state !== 'human') return 'owner';
  // Switched off for this chat: the server keeps Leo out until Back to Leo, cover included (Gary, 29 Oct: Jacques).
  if (c.ai_handover_reason === 'switched_off') return 'owner';
  const since = c.qualifier?.leo_cover_at;
  const covering = !!since && !rows.some((m) => m.direction === 'out' && m.sender === 'operator' && m.created_at > since);
  const ownerAway = !!c.assigned_user_id && snap.away.has(c.assigned_user_id);
  return covering || ownerAway || !isOpen(now, snap.holidays) ? 'leo' : 'owner';
}

function toChat(c: ConversationRow, snap: Snapshot, rows: MessageRow[], me: string, local: LocalState, now: number, earlier: MessageRow[] = []): Chat {
  const identity = c.identity_id ? snap.identities.get(c.identity_id) : undefined;
  const email = c.channel === 'email' ? identity?.external_id : undefined;
  const dm = c.channel === 'facebook' || c.channel === 'instagram' ? c.channel : undefined;
  const dmPage = dm && typeof c.source?.page === 'string' ? c.source.page : undefined;
  const inboundAge = c.last_inbound_at ? now - new Date(c.last_inbound_at).getTime() : Infinity;
  const state = snap.states.get(c.id);
  // WhatsApp's name first, then the customer or lead record's, then the number (Gary, 15 Oct).
  const name = identity?.display_name?.trim()
    || (c.customer_id ? snap.customerNames.get(c.customer_id) : '')
    || (c.lead_id ? snap.leadNames.get(c.lead_id) : '')
    || email || identity?.phone_e164 || (dm === 'instagram' ? 'Instagram user' : dm ? 'Facebook user' : 'Customer');
  const territory = c.territory_id ? snap.territories.get(c.territory_id) : undefined;
  const lastRead = state?.last_read_at ? new Date(state.last_read_at).getTime() : 0;
  const byId = new Map([...earlier, ...rows].map((m) => [m.id, m]));

  const thread: ThreadItem[] = [];
  let lastDay = '';
  let earlierItems = 0;
  const lead = leadOf(c.qualifier);
  for (const m of [...earlier, ...rows]) {
    if (m.kind === 'reaction') continue; // shown on the message it reacts to
    if (snap.marks.get(m.id)?.hidden) continue; // Delete for me
    if (m.id.startsWith('current-')) {
      // Everything before "This chat" is folded away; this chat starts with its own date.
      thread.push({ id: m.id, from: 'system', body: { kind: 'text', text: m.body ?? '' }, at: hhmm(m.created_at), assistant: isLeoNote(m.body) || undefined });
      earlierItems = thread.length;
      lastDay = '';
      continue;
    }
    const day = dayLabel(m.created_at);
    if (day !== lastDay) {
      thread.push({ id: `day-${m.id}`, from: 'day', label: day });
      lastDay = day;
    }
    if (isLeadCard(m.provider_message_id)) {
      thread.push({ id: m.id, from: 'lead', text: m.body ?? '', at: hhmm(m.created_at) });
      continue;
    }
    const quoted = m.reply_to_message_id ? byId.get(m.reply_to_message_id) : undefined;
    const from: ChatMessage['from'] = m.sender === 'customer' ? 'in' : m.sender === 'auto' ? 'auto' : m.sender === 'note' ? 'note' : m.sender === 'system' ? 'system' : 'out';
    const author = m.sent_by_user_id && m.sent_by_user_id !== me ? snap.people.get(m.sent_by_user_id) : undefined;
    thread.push({
      id: m.id,
      from,
      body: bodyOf(m, snap.media),
      approved: m.kind === 'template' || undefined,
      buttons: m.kind === 'template'
        ? splitButtons(m.body ?? '').buttons
        : m.kind === 'interactive' && m.direction === 'out' && splitLinkButton(m.body ?? '').button ? [splitLinkButton(m.body ?? '').button!] : undefined,
      at: hhmm(m.created_at),
      delivery: from === 'out' || from === 'auto' ? deliveryOf(m.delivery_status) : undefined,
      failedReason: m.delivery_status === 'failed' ? (m.error ?? undefined) : undefined,
      signOff: m.sign_off ?? undefined,
      email: m.email_meta ? { subject: m.email_meta.subject ?? undefined } : undefined,
      author: m.sender === 'assistant' ? 'Leo' : author,
      // Leo's messages, and system notes by or about Leo, in Leo's colour (Gary, 18 Oct).
      assistant: m.sender === 'assistant' || (from === 'system' && isLeoNote(m.body)) || undefined,
      theirReaction: m.reaction ?? undefined,
      reaction: m.our_reaction ?? undefined,
      starred: snap.marks.get(m.id)?.starred || undefined,
      replyTo: quoted ? {
        id: quoted.id,
        author: quoted.sender === 'customer' ? name : quoted.sender === 'auto' ? 'Auto reply' : 'You',
        preview: quoted.body ?? (quoted.kind === 'image' ? '📷 Photo' : quoted.kind === 'audio' ? '🎤 Voice note' : 'Message'),
      } : undefined,
    });
  }

  const unread = rows.filter((m) => m.direction === 'in' && m.kind !== 'reaction' && new Date(m.created_at).getTime() > lastRead).length;
  const last = rows[rows.length - 1];
  const due = c.reply_due_at ? new Date(c.reply_due_at).getTime() : null;
  const timed = c.status === 'waiting_operator' && due !== null;
  const assignedName = c.assigned_user_id ? snap.people.get(c.assigned_user_id) : undefined;
  const inbox = INBOX[c.inbox];
  const unpaid = !!c.customer_id && snap.unpaid.has(c.customer_id);
  const paid = !unpaid && !!c.customer_id && snap.paid.has(c.customer_id);
  // Activity is a person talking: the customer, the team or Leo. Automatic messages (booking confirmations,
  // payment receipts, reminders) and system lines do not move a chat up the list (Gary, 19 Oct: they flooded it).
  const lastReal = [...rows].reverse().find((m) => (m.direction === 'in' && m.kind !== 'reaction') || (m.direction === 'out' && m.sender !== 'auto'));
  const lastActivityAt = lastReal?.created_at ?? last?.created_at ?? c.updated_at;

  return {
    id: c.id,
    brand: c.brand,
    name,
    area: c.qualifier?.suburb || territory || '',
    // The suburb the customer gave first, so nobody asks again (Gary, 16 Oct).
    detail: [c.qualifier?.suburb, email ?? identity?.phone_e164, dm ? `${dm === 'facebook' ? 'Facebook' : 'Instagram'}${dmPage ? `: ${dmPage}` : ''}` : '', territory].filter(Boolean).join(' · ') || undefined,
    colour: colourOf(c.id),
    state: c.qualifier_step ? 'qualifying' : c.status === 'unassigned' ? 'new' : c.status === 'waiting_operator' ? 'mine' : c.status === 'booked' ? 'booked' : 'waiting',
    unread,
    time: listTime(last?.created_at ?? c.updated_at),
    // Head office's flag is never shown to the operator (Gary, 16 Oct): only how long the
    // customer has been waiting.
    dueMinutes: timed ? Math.max(0, Math.ceil((due - now) / 60000)) : undefined,
    dueAt: timed && c.reply_due_at ? c.reply_due_at : undefined,
    waitingSince: timed ? c.last_inbound_at ?? undefined : undefined,
    busyRemindAt: c.busy_remind_at ?? undefined,
    territoryId: c.territory_id ?? undefined,
    answered: c.status !== 'waiting_operator' && !!c.first_reply_at,
    closed: c.status === 'closed' || undefined,
    // Email has no 24-hour rule (Gary, 19 Oct).
    // Facebook and Instagram: a person may reply for 7 days (Meta's Human Agent rule, Gary 3 Nov 2026).
    windowOpen: !!email || inboundAge < (dm ? 7 * DAY_MS : DAY_MS),
    dm,
    dmPage,
    dmHumanOnly: (dm && inboundAge >= DAY_MS && inboundAge < 7 * DAY_MS) || undefined,
    inbox,
    owner: assignedName,
    leoCovering: (() => {
      const since = c.qualifier?.leo_cover_at;
      return !!since && !rows.some((m) => m.direction === 'out' && m.sender === 'operator' && m.created_at > since);
    })(),
    ownerId: c.assigned_user_id ?? undefined,
    onDuty: onDutyOf(c, snap, rows, !!email || !!dm, now),
    takenOver: c.assigned_user_id === me,
    signOff: c.inbox === 'leads' ? undefined : SIGN_OFF[c.brand][c.inbox],
    unpaid,
    paid,
    tier: c.customer_id ? snap.customerTiers.get(c.customer_id) : undefined,
    leoOff: (!!c.customer_id && snap.leoOff.has(`c:${c.customer_id}`)) || (!!c.lead_id && snap.leoOff.has(`l:${c.lead_id}`)),
    grade: c.grade ?? undefined,
    lastActivityAt,
    lastInboundAt: c.last_inbound_at ?? undefined,
    // Archived by hand, closed, or quiet for 7 days with nothing owed (Gary, 16 Oct).
    archived: !!state?.archived_at || c.status === 'closed' || autoArchived({ unpaid, lastActivityAt }, now),
    pinned: !!state?.pinned_at,
    muted: !!state?.muted_until && new Date(state.muted_until).getTime() > now,
    // Kept (Gary, 20 Oct): marked unread until read again; this device's last tap shows before the save comes back.
    markedUnread: (local.markedUnread.get(c.id) ?? markedSince(state)) && unread === 0,
    lead,
    phone: identity?.phone_e164 ?? undefined,
    // Once the customer replies by email it is an email chat and can be answered (Gary, 19 Oct).
    emailLead: (c.source?.email_lead === true && !email) || undefined,
    email,
    infoInbox: (email && c.source?.kind === 'info_email') || undefined,
    subject: email ? [...rows].reverse().find((m) => m.direction === 'in' && m.email_meta?.subject)?.email_meta?.subject ?? undefined : undefined,
    customerId: c.customer_id ?? undefined,
    leadId: c.lead_id ?? undefined,
    aiState: c.ai_state ?? 'off',
    aiHandover: c.ai_handover_reason ?? undefined,
    quickReplies: [],
    labels: (snap.chatLabels.get(c.id) ?? []).map((id) => snap.labels.find((l) => l.id === id)).filter((l): l is ChatLabel => !!l),
    thread,
    earlier: earlierItems && earlier[0] ? {
      items: earlierItems,
      chats: earlier.filter((m) => m.id.startsWith('earlier-')).length,
      since: new Date(earlier[0].created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }),
    } : undefined,
  };
}

interface LocalState { markedUnread: Map<string, boolean> }

/** Marked unread and not read since: reading the chat clears the mark. */
function markedSince(state: StateRow | undefined): boolean {
  return !!state?.marked_unread_at;
}

/**
 * Team chats (Gary, 15 Oct): this person's one-to-one chats with colleagues (team_threads,
 * row rules: members only) and everyone with access for the All tab. Staff only, never WhatsApp.
 */
/** Sentinel-owned channels where head office writes to the AI team (supabase/functions/_shared/management-channel.ts). */
const AI_TEAM_CHANNELS = ['management', 'assistant_ted', 'assistant_marketing', 'assistant_chief_of_staff', 'assistant_finance'];
const AI_TEAM_NAMES: Record<string, string> = { leo: 'Leo', ...ASSISTANT_NAMES };

/** An AI team channel's avatar: CMB Management its own, an assistant's channel its assistant, Leo's review channel Leo. */
function aiAvatarOf(systemKey: string | null): AiTeamMember | 'management' | undefined {
  if (systemKey === 'management') return 'management';
  if (systemKey === 'leo_review') return 'leo';
  const role = systemKey?.startsWith('assistant_') ? systemKey.slice('assistant_'.length) : null;
  return role && role in ASSISTANT_NAMES ? (role as AiTeamMember) : undefined;
}

async function loadTeam(me: string, headOffice: boolean): Promise<{ team: TeamChat[]; people: TeamPerson[] }> {
  // Joins any channel meant for everyone first (team_join_channels), so new staff see it.
  await supabase.rpc('team_join_channels');
  const [mem, threads, people, everyone, roleFlags, coachingRes, testRes] = await Promise.all([
    supabase.from('team_thread_members').select('thread_id, user_id, last_read_at, archived_at, muted_until, role'),
    supabase.from('team_threads').select('id, last_message_at, last_preview, kind, name, description, system_key'),
    loadPeople({ withMobile: true }),
    loadPeople(),
    // Assistants switched off show their channel read-only, as Coming soon (Gary, 1 Nov). Super admins only can read this.
    headOffice ? supabase.from('ask_roles').select('role, active') : Promise.resolve({ data: [] }),
    // Groups Ted coaches get their own tab (Gary, 1 Nov 2026); the database decides who may see which.
    supabase.rpc('ted_coaching_threads'),
    // Which coaching groups are in test mode (head office only can read this).
    headOffice ? supabase.from('ted_coaching').select('thread_id, test').eq('test', true) : Promise.resolve({ data: [] }),
  ]);
  const testThreads = new Set(((testRes.data ?? []) as { thread_id: string }[]).map((r) => r.thread_id));
  const coachingThreads = new Set(((coachingRes.data ?? []) as unknown[]).map((v) => String(typeof v === 'object' && v !== null ? Object.values(v)[0] : v)));
  const offAssistantChannels = new Set(((roleFlags.data ?? []) as { role: string; active: boolean }[]).filter((r) => !r.active).map((r) => `assistant_${r.role}`));
  const members = (mem.data ?? []) as { thread_id: string; user_id: string; last_read_at: string | null; archived_at: string | null; muted_until: string | null; role: 'member' | 'admin' }[];
  const list = (threads.data ?? []) as { id: string; last_message_at: string | null; kind: 'direct' | 'group' | 'channel'; name: string | null; description: string | null; system_key: string | null }[];
  const ids = list.map((t) => t.id);
  const none = Promise.resolve({ data: [] });
  const [msgRes, markRes, reactRes] = await Promise.all([
    ids.length ? supabase.from('team_messages').select('id, thread_id, sender_id, body, forwarded, created_at, reply_to_id, kind, media_path, media_name, edited_at, deleted_at, done_at, done_by, assistant, visibility, post_type, post_result').in('thread_id', ids).order('created_at').limit(3000) : none,
    ids.length ? supabase.from('team_message_marks').select('message_id, starred, hidden').in('thread_id', ids) : none,
    ids.length ? supabase.from('team_message_reactions').select('message_id, user_id, emoji').in('thread_id', ids) : none,
  ]);
  const msgs = (msgRes.data ?? []) as {
    id: string; thread_id: string; sender_id: string | null; body: string; forwarded: boolean; created_at: string;
    reply_to_id: string | null; kind: 'text' | 'image' | 'document' | 'audio' | 'system'; media_path: string | null; media_name: string | null;
    edited_at: string | null; deleted_at: string | null; done_at: string | null; done_by: string | null; assistant: string | null; visibility?: string;
    post_type?: CoachPostType | null; post_result?: string | null;
  }[];
  const marks = new Map(((markRes.data ?? []) as { message_id: string; starred: boolean; hidden: boolean }[]).map((m) => [m.message_id, m]));
  const reactions = (reactRes.data ?? []) as { message_id: string; user_id: string; emoji: string }[];
  // Photos and PDFs: short-lived links to the private team store (members only).
  const paths = msgs.map((m) => m.media_path).filter((p): p is string => !!p);
  const links = new Map<string, string>();
  if (paths.length) {
    const { data } = await supabase.storage.from('team-media').createSignedUrls(paths, MEDIA_TTL_S);
    for (const s of data ?? []) if (s.path && s.signedUrl) links.set(s.path, s.signedUrl);
  }
  const byId = new Map(msgs.map((m) => [m.id, m]));
  // Names of existing chats come from everyone, so a chat stays named if a mobile is removed.
  const nameOf = new Map(everyone.map((p) => [p.id, p.name]));
  const team: TeamChat[] = [];
  for (const t of list) {
    const mine = members.find((m) => m.thread_id === t.id && m.user_id === me);
    const other = members.find((m) => m.thread_id === t.id && m.user_id !== me);
    const isDirect = t.kind === 'direct';
    if (!mine || (isDirect && !other)) continue;
    const inThread = members.filter((m) => m.thread_id === t.id);
    const rows = msgs.filter((m) => m.thread_id === t.id);
    const thread: ThreadItem[] = [];
    const aiChannel = !!t.system_key && AI_TEAM_CHANNELS.includes(t.system_key);
    let lastDay = '';
    for (const m of rows) {
      if (marks.get(m.id)?.hidden) continue; // Delete for me
      const day = dayLabel(m.created_at);
      if (day !== lastDay) { thread.push({ id: `day-${m.id}`, from: 'day', label: day }); lastDay = day; }
      // "Gary changed the group name to ...", in the thread's system-line style (Gary, 17 Oct).
      if (m.kind === 'system') {
        thread.push({ id: m.id, from: 'system', body: { kind: 'text', text: systemLine(m.sender_id === me, nameOf.get(m.sender_id ?? ''), m.body) }, at: hhmm(m.created_at), sentAt: m.created_at });
        continue;
      }
      const url = m.media_path ? links.get(m.media_path) : undefined;
      const quoted = m.reply_to_id ? byId.get(m.reply_to_id) : undefined;
      // An AI team post (Gary, 1 Nov): its writer's name as the author, the "Ted: " prefix taken off the text.
      const aiName = !m.sender_id && m.assistant ? AI_TEAM_NAMES[m.assistant] : undefined;
      // Ted's coaching posts carry a chip and title (Gary, 2 Nov 2026) and may be marked done by the group.
      const coachPost = coachingThreads.has(t.id) && m.assistant === 'ted' && m.post_type && !m.deleted_at
        ? { type: m.post_type, title: `${COACH_POST_NAMES[m.post_type]} · ${shortDate(m.created_at)}`, result: m.post_result ?? '' }
        : undefined;
      // An assistant's post in its AI team channel (Gary, 3 Nov 2026): Done folds it to its first line.
      const aiPost = aiChannel && aiName && !m.deleted_at
        ? { headline: ((m.body.replace(new RegExp(`^${aiName}: `), '').split('\n').find((l) => l.trim()) ?? '').replace(/\*\*/g, '').trim().slice(0, 120)) }
        : undefined;
      thread.push({
        id: m.id,
        from: m.sender_id === me ? 'out' : 'in',
        // Deleted for everyone by its sender (Gary, 16 Oct): its place stays, nothing of it.
        body: m.deleted_at
          ? { kind: 'text', text: 'This message was deleted' }
          : m.kind === 'image'
          ? { kind: 'photo', caption: m.body, url }
          : m.kind === 'audio'
            ? { kind: 'voice', duration: '', url }
          : m.kind === 'document'
            ? (url ? { kind: 'link', title: m.media_name || 'Document', url, text: m.body, file: fileLabel('document', null, m.media_name) } : { kind: 'text', text: m.body || m.media_name || 'Document' })
            : { kind: 'text', text: aiName ? m.body.replace(new RegExp(`^${aiName}: `), '') : m.body },
        at: hhmm(m.created_at),
        forwarded: (m.forwarded && !m.deleted_at) || undefined,
        teamNote: m.visibility === 'head_office' || undefined,
        edited: (!!m.edited_at && !m.deleted_at) || undefined,
        deleted: !!m.deleted_at || undefined,
        sentAt: m.created_at,
        delivery: m.sender_id === me ? 'delivered' : undefined,
        starred: marks.get(m.id)?.starred || undefined,
        // Platform Health: Done takes a post out of the channel for everyone (Gary, 20 Oct).
        doneable: (!!t.system_key && !aiChannel && !m.deleted_at) || !!coachPost || !!aiPost || undefined,
        aiPost,
        coachPost,
        done: (t.system_key || coachPost) && m.done_at ? { by: m.done_by === me ? 'you' : nameOf.get(m.done_by ?? '') ?? 'a colleague', at: listTime(m.done_at) } : undefined,
        // In a group or channel each message carries its sender's name, as WhatsApp groups do.
        // In Platform Health the poster is Sentinel itself (Gary, 17 Oct).
        // No sender: Sentinel in its own channels, Leo in the others (Gary, 22 Oct 2026: Leo's welcome in CleanMyBed News).
        author: !isDirect && m.sender_id !== me ? (!m.sender_id ? (aiName ?? (t.system_key ? 'Sentinel' : 'Leo')) : nameOf.get(m.sender_id) ?? 'Colleague') : undefined,
        aiTeam: (!!aiName && !m.deleted_at) || undefined,
        aiMember: aiName ? (m.assistant as AiTeamMember) : undefined,
        reaction: reactions.find((r) => r.message_id === m.id && r.user_id === me)?.emoji,
        theirReaction: reactions.find((r) => r.message_id === m.id && r.user_id !== me)?.emoji,
        // Leo's posts in a channel carry his name in violet, as in a customer chat (22 Oct 2026).
        assistant: (!isDirect && !m.sender_id && !t.system_key) || undefined,
        // In a group or channel everyone sees every reaction and who gave it (Gary, 22 Oct 2026: a read receipt and a poll).
        reactionsAll: isDirect ? undefined : reactionGroups(reactions.filter((r) => r.message_id === m.id), me, nameOf),
        replyTo: quoted ? {
          id: quoted.id,
          author: quoted.sender_id === me ? 'You' : nameOf.get(quoted.sender_id ?? '') ?? 'Colleague',
          preview: quoted.deleted_at ? 'This message was deleted' : quoted.body || (quoted.kind === 'image' ? '📷 Photo' : quoted.kind === 'audio' ? '🎤 Voice note' : quoted.kind === 'document' ? `📄 ${quoted.media_name ?? 'Document'}` : ''),
        } : undefined,
      });
    }
    const readAt = mine.last_read_at ? new Date(mine.last_read_at).getTime() : 0;
    team.push({
      id: t.id,
      name: isDirect ? nameOf.get(other!.user_id) ?? 'Colleague' : t.name ?? (t.kind === 'channel' ? 'Channel' : 'Group'),
      colour: colourOf(isDirect ? other!.user_id : t.id),
      kind: t.kind,
      aiAvatar: aiAvatarOf(t.system_key) ?? (coachingThreads.has(t.id) ? 'ted' : undefined),
      coaching: coachingThreads.has(t.id) || undefined,
      coachTest: testThreads.has(t.id) || undefined,
      description: t.description ?? undefined,
      members: inThread.map((m) => ({ id: m.user_id, name: m.user_id === me ? 'You' : nameOf.get(m.user_id) ?? 'Colleague', admin: m.role === 'admin' })),
      amAdmin: mine.role === 'admin',
      // The AI team's channels (Gary, 1 Nov): Gary and Charlie write to the assistants there; the server checks super admin.
      canPost: t.system_key ? AI_TEAM_CHANNELS.includes(t.system_key) && headOffice && !offAssistantChannels.has(t.system_key) : (t.kind !== 'channel' || mine.role === 'admin' || headOffice),
      system: !!t.system_key || undefined,
      time: t.last_message_at ? listTime(t.last_message_at) : '',
      thread,
      // A system line (a new name) is not a message to read, as WhatsApp.
      // A post marked done needs nobody (Platform Health, Gary 20 Oct).
      unread: rows.filter((m) => m.sender_id !== me && m.kind !== 'system' && !m.done_at && new Date(m.created_at).getTime() > readAt).length,
      archived: !!mine.archived_at,
      personId: isDirect ? other!.user_id : undefined,
      muted: !!mine.muted_until && new Date(mine.muted_until).getTime() > Date.now(),
    });
  }
  team.sort((a, b) => {
    const at = list.find((t) => t.id === a.id)?.last_message_at ?? '';
    const bt = list.find((t) => t.id === b.id)?.last_message_at ?? '';
    return at < bt ? 1 : -1;
  });
  return { team, people: people.filter((p) => p.id !== me).map((p) => ({ id: p.id, name: p.name })) };
}

async function loadSnapshot(previous: Map<string, { url: string; until: number }>): Promise<Snapshot> {
  const { data: convs, error } = await supabase.from('conversations')
    .select('id, channel, brand, identity_id, territory_id, assigned_user_id, inbox, status, qualifier_step, qualifier, last_inbound_at, first_reply_at, reply_due_at, escalated_at, updated_at, customer_id, lead_id, ai_state, ai_handover_reason, busy_remind_at, source, grade')
    .order('updated_at', { ascending: false }).limit(300);
  if (error) throw new Error(error.message);
  const conversations = (convs ?? []) as ConversationRow[];
  const ids = conversations.map((c) => c.id);
  const empty: Snapshot = { conversations, messages: [], identities: new Map(), territories: new Map(), people: new Map(), customerNames: new Map(), leadNames: new Map(), states: new Map(), media: new Map(), labels: [], chatLabels: new Map(), marks: new Map(), unpaid: new Set(), paid: new Set(), customerTiers: new Map(), leoOff: new Set(), away: new Set(), holidays: new Set() };
  if (!ids.length) return empty;

  const identityIds = [...new Set(conversations.map((c) => c.identity_id).filter((x): x is string => !!x))];
  const territoryIds = [...new Set(conversations.map((c) => c.territory_id).filter((t): t is string => !!t))];
  const customerIds = [...new Set(conversations.map((c) => c.customer_id).filter((x): x is string => !!x))];
  const leadIds = [...new Set(conversations.map((c) => c.lead_id).filter((x): x is string => !!x))];
  const none = Promise.resolve({ data: [], error: null });
  const [msgs, idents, terrs, states, custs, leadRows, labelRows, onChats, markRows, owing, settled] = await Promise.all([
    supabase.from('conversation_messages')
      .select('id, conversation_id, direction, sender, kind, body, media_path, reply_to_message_id, reaction, our_reaction, sign_off, delivery_status, error, sent_by_user_id, created_at, provider_message_id, email_meta')
      .in('conversation_id', ids).order('created_at', { ascending: true }).limit(5000),
    identityIds.length ? supabase.from('contact_identities').select('id, display_name, phone_e164, external_id').in('id', identityIds) : none,
    territoryIds.length ? supabase.from('territories').select('id, name').in('id', territoryIds) : Promise.resolve({ data: [], error: null }),
    supabase.from('conversation_user_state').select('conversation_id, last_read_at, archived_at, pinned_at, muted_until, marked_unread_at').in('conversation_id', ids),
    // Row rules apply: where a login cannot read the record, the chat shows the number instead.
    customerIds.length ? supabase.from('customers').select('id, first_name, last_name, loyalty_tier, total_services, leo_off_at').in('id', customerIds) : none,
    leadIds.length ? supabase.from('leads').select('id, first_name, last_name, leo_off_at').in('id', leadIds) : none,
    supabase.from('chat_labels').select('id, name, colour').eq('active', true).order('sort').order('name'),
    supabase.from('conversation_labels').select('conversation_id, label_id').in('conversation_id', ids),
    supabase.from('message_user_state').select('message_id, starred, hidden').in('conversation_id', ids),
    customerIds.length
      ? supabase.from('invoices').select('customer_id').in('customer_id', customerIds).in('status', ['sent', 'overdue']).gt('balance_due', 0)
      : none,
    customerIds.length
      ? supabase.from('invoices').select('customer_id').in('customer_id', customerIds).eq('status', 'paid').gte('paid_at', new Date(Date.now() - 30 * 86_400_000).toISOString())
      : none,
  ]);
  const unpaid = new Set(((owing.data ?? []) as { customer_id: string }[]).map((r) => r.customer_id));
  const paid = new Set(((settled.data ?? []) as { customer_id: string }[]).map((r) => r.customer_id));
  const chatLabels = new Map<string, string[]>();
  for (const r of (onChats.data ?? []) as { conversation_id: string; label_id: string }[]) {
    chatLabels.set(r.conversation_id, [...(chatLabels.get(r.conversation_id) ?? []), r.label_id]);
  }
  if (msgs.error) throw new Error(msgs.error.message);
  const messages = (msgs.data ?? []) as MessageRow[];

  const personIds = [...new Set([
    ...conversations.map((c) => c.assigned_user_id),
    ...messages.map((m) => m.sent_by_user_id),
  ].filter((p): p is string => !!p))];
  const people = new Map<string, string>();
  const away = new Set<string>();
  if (personIds.length) {
    const { data } = await supabase.from('users').select('id, first_name, last_name, chats_away_at').in('id', personIds);
    for (const p of (data ?? []) as (NameRow & { chats_away_at: string | null })[]) {
      people.set(p.id, [p.first_name, p.last_name].filter(Boolean).join(' '));
      if (p.chats_away_at) away.add(p.id);
    }
  }
  // Public holidays, for who is on duty (Leo all day on a holiday).
  const { data: cfg } = await supabase.from('country_config').select('public_holidays').limit(1).maybeSingle();
  const holidays = new Set(((cfg?.public_holidays ?? []) as { date?: string }[]).map((h) => h.date).filter((d): d is string => !!d));

  // Photos and voice notes: short-lived links, reused while still fresh.
  const media = new Map<string, string>();
  const now = Date.now();
  const toSign: string[] = [];
  for (const m of messages) {
    if (!m.media_path || m.media_path.startsWith('https://')) continue;
    const cached = previous.get(m.media_path);
    if (cached && cached.until > now) media.set(m.media_path, cached.url);
    else toSign.push(m.media_path);
  }
  if (toSign.length) {
    const { data } = await supabase.storage.from('conversation-media').createSignedUrls(toSign, MEDIA_TTL_S);
    for (const s of data ?? []) {
      if (s.path && s.signedUrl) {
        media.set(s.path, s.signedUrl);
        previous.set(s.path, { url: s.signedUrl, until: now + (MEDIA_TTL_S - 60) * 1000 });
      }
    }
  }

  return {
    conversations,
    messages,
    identities: new Map(((idents.data ?? []) as IdentityRow[]).map((i) => [i.id, i])),
    territories: new Map(((terrs.data ?? []) as { id: string; name: string }[]).map((t) => [t.id, t.name])),
    people,
    away,
    holidays,
    customerNames: new Map(((custs.data ?? []) as NameRow[]).map((r) => [r.id, fullName(r)])),
    leadNames: new Map(((leadRows.data ?? []) as NameRow[]).map((r) => [r.id, fullName(r)])),
    states: new Map(((states.data ?? []) as StateRow[]).map((s) => [s.conversation_id, s])),
    labels: (labelRows.data ?? []) as ChatLabel[],
    chatLabels,
    marks: new Map(((markRows.data ?? []) as { message_id: string; starred: boolean; hidden: boolean }[]).map((m) => [m.message_id, { starred: m.starred, hidden: m.hidden }])),
    media,
    unpaid,
    paid,
    // A customer never serviced yet (booked, not done) has no tier: they show as New (Gary, 18 Oct).
    customerTiers: new Map(((custs.data ?? []) as { id: string; loyalty_tier: string | null; total_services: number | null }[])
      .filter((r) => TIERS.includes(r.loyalty_tier as Tier) && Number(r.total_services ?? 0) > 0).map((r) => [r.id, r.loyalty_tier as Tier])),
    leoOff: new Set([
      ...((custs.data ?? []) as { id: string; leo_off_at: string | null }[]).filter((r) => r.leo_off_at).map((r) => `c:${r.id}`),
      ...((leadRows.data ?? []) as { id: string; leo_off_at: string | null }[]).filter((r) => r.leo_off_at).map((r) => `l:${r.id}`),
    ]),
  };
}

const TIERS: Tier[] = ['amber', 'bronze', 'silver', 'gold', 'platinum'];

/** The send function's own message, e.g. "Take this chat over before replying". */
async function errorText(error: unknown): Promise<string> {
  if (error instanceof FunctionsHttpError) {
    try {
      const body = await error.context.json() as { error?: string };
      if (body.error) return body.error;
    } catch {
      // not JSON: fall through
    }
  }
  return 'Something went wrong. Try again.';
}

export function LiveChatsProvider({ userId, children }: { userId: string; children: ReactNode }) {
  const { toast } = useOverlay();
  const { access } = useAuth();
  const headOffice = access.role === 'head_office';
  const [snap, setSnap] = useState<Snapshot | null>(null);
  const [teamData, setTeamData] = useState<{ team: TeamChat[]; people: TeamPerson[] }>({ team: [], people: [] });
  const [sending, setSending] = useState(false);
  const [now, setNow] = useState(() => Date.now());
  const [markedUnread, setMarkedUnread] = useState<Map<string, boolean>>(new Map());
  // A tap is held here only until a reload shows it saved, so another device's change is never hidden.
  useEffect(() => {
    if (!snap) return;
    setMarkedUnread((m) => {
      const left = [...m].filter(([id, on]) => markedSince(snap.states.get(id)) !== on);
      return left.length === m.size ? m : new Map(left);
    });
  }, [snap]);
  const [keepArchived, setKeepArchivedState] = useState(readKeepArchived);
  const mediaCache = useRef(new Map<string, { url: string; until: number }>());
  const reloadTimer = useRef<number | null>(null);

  // Customer messages already seen, so only a new one plays the message sound.
  const heard = useRef<Set<string> | null>(null);

  const reload = useCallback(async () => {
    try {
      const [next, nextTeam] = await Promise.all([loadSnapshot(mediaCache.current), loadTeam(userId, headOffice)]);
      setTeamData(nextTeam);
      const inbound = next.messages.filter((m) => m.direction === 'in' && m.kind !== 'reaction').map((m) => m.id);
      if (heard.current && inbound.some((m) => !heard.current?.has(m))) playChime();
      heard.current = new Set(inbound);
      setSnap(next);
    } catch (e) {
      console.error('Live chats failed to load', e);
      toast('Chats could not load. Check your connection.');
    }
  }, [toast, userId, headOffice]);

  // Changes arrive in bursts (a message, then its ticks): one reload per burst.
  const reloadSoon = useCallback(() => {
    if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
    reloadTimer.current = window.setTimeout(() => void reload(), 300);
  }, [reload]);

  useEffect(() => {
    void reload();
    const channel = supabase.channel(`chats-live-${userId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversations' }, reloadSoon)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'conversation_messages' }, reloadSoon)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_messages' }, reloadSoon)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'team_message_reactions' }, reloadSoon)
      .subscribe();
    // Reply timers count down between changes; coming back to the app catches up at once.
    const tick = window.setInterval(() => setNow(Date.now()), 30_000);
    const onVisible = () => { if (document.visibilityState === 'visible') { setNow(Date.now()); void reload(); } };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      void supabase.removeChannel(channel);
      window.clearInterval(tick);
      document.removeEventListener('visibilitychange', onVisible);
      if (reloadTimer.current) window.clearTimeout(reloadTimer.current);
    };
  }, [reload, reloadSoon, userId]);

  const chats = useMemo(() => {
    if (!snap) return [];
    const byConversation = new Map<string, MessageRow[]>();
    for (const m of snap.messages) {
      const list = byConversation.get(m.conversation_id);
      if (list) list.push(m); else byConversation.set(m.conversation_id, [m]);
    }
    return onePerPerson(snap.conversations).map((c) => toChat(c, snap, byConversation.get(c.id) ?? [], userId, { markedUnread }, now, earlierRows(c, snap.conversations, byConversation)))
      .sort((a, b) => (b.lastActivityAt ?? '').localeCompare(a.lastActivityAt ?? ''));
  }, [snap, userId, markedUnread, now]);

  const call = useCallback(async (body: Record<string, unknown> | FormData): Promise<boolean> => {
    const { error } = await supabase.functions.invoke('messaging-send', { body });
    if (error) {
      toast(await errorText(error));
      return false;
    }
    reloadSoon();
    return true;
  }, [toast, reloadSoon]);

  const saveState = useCallback(async (ids: string[], patch: Partial<Omit<StateRow, 'conversation_id'>>) => {
    const rows = ids.map((id) => ({ conversation_id: id, user_id: userId, ...patch }));
    const { error } = await supabase.from('conversation_user_state').upsert(rows, { onConflict: 'conversation_id,user_id' });
    if (error) toast('That did not save. Try again.');
    else void reload();
  }, [userId, toast, reload]);

  const saveMark = useCallback(async (chatId: string, messageId: string, patch: { starred?: boolean; hidden?: boolean }): Promise<boolean> => {
    const { error } = await supabase.from('message_user_state').upsert(
      { user_id: userId, message_id: messageId, conversation_id: chatId, updated_at: new Date().toISOString(), ...patch },
      { onConflict: 'user_id,message_id' },
    );
    if (error) { toast('That did not save. Try again.'); return false; }
    void reload();
    return true;
  }, [userId, toast, reload]);

  const setKeepArchived = useCallback((on: boolean) => {
    setKeepArchivedState(on);
    try {
      localStorage.setItem('chats.keepArchived', on ? '1' : '0');
    } catch {
      // Storage blocked: the setting still holds for this visit.
    }
  }, []);

  const markRead = useCallback((chatId: string) => {
    setMarkedUnread((m) => new Map(m).set(chatId, false));
    void call({ action: 'read', conversation_id: chatId });
    if (markedSince(snap?.states.get(chatId))) void saveState([chatId], { marked_unread_at: null });
  }, [call, snap, saveState]);

  const setRead = useCallback((chatIds: string[], read: boolean) => {
    if (read) { chatIds.forEach((id) => markRead(id)); return; }
    setMarkedUnread((m) => { const n = new Map(m); chatIds.forEach((id) => n.set(id, true)); return n; });
    void saveState(chatIds, { marked_unread_at: new Date().toISOString() });
  }, [markRead, saveState]);

  const value: ChatsStore = {
    live: true,
    sending,
    chats,
    team: teamData.team,
    teamPeople: teamData.people,
    openTeam: async (personId) => {
      const { data, error } = await supabase.rpc('team_open_direct', { p_other: personId });
      if (error) { toast('That chat could not open. Try again.'); return null; }
      void reload();
      return data as string;
    },
    markTeamDone: (messageId, done) => {
      void call({ action: 'team_done', message_id: messageId, done }).then((ok) => { if (ok) void reload(); });
    },
    markTeamRead: (teamId) => {
      void supabase.from('team_thread_members').update({ last_read_at: new Date().toISOString() })
        .eq('thread_id', teamId).eq('user_id', userId).then(() => void reload());
    },
    createGroup: async (name, memberIds) => {
      const { data, error } = await supabase.rpc('team_create_group', { p_name: name, p_members: memberIds });
      if (error) return { error: error.message };
      await reload();
      return { id: data as string };
    },
    addTeamMembers: async (teamId, memberIds) => {
      const { error } = await supabase.rpc('team_add_members', { p_thread: teamId, p_members: memberIds });
      if (error) return error.message;
      void reload();
      return null;
    },
    removeTeamMember: async (teamId, personId) => {
      const { error } = await supabase.rpc('team_remove_member', { p_thread: teamId, p_user: personId });
      if (error) return error.message;
      void reload();
      return null;
    },
    setTeamAdmin: async (teamId, personId, admin) => {
      const { error } = await supabase.rpc('team_set_admin', { p_thread: teamId, p_user: personId, p_admin: admin });
      if (error) return error.message;
      void reload();
      return null;
    },
    editTeamInfo: async (teamId, name, description) => {
      // The server checks who may (messaging-send team_info); the new name shows once reloaded.
      const ok = await call({ action: 'team_info', thread_id: teamId, name, description });
      if (ok) await reload();
      return ok;
    },
    setGroupCoach: async (teamId, personIds, on) => {
      const { error } = await supabase.rpc('ted_coaching_set', { p_thread_id: teamId, p_scope: personIds, p_active: on });
      if (error) return /Pick who|operators in this group|Only a group/.test(error.message) ? error.message : 'That did not save. Try again.';
      await reload();
      return null;
    },
    createChannel: async (name) => {
      const { data, error } = await supabase.rpc('team_create_channel', { p_name: name, p_everyone: true });
      if (error) return { error: error.message };
      await reload();
      return { id: data as string };
    },
    setTeamMuted: (teamId, muted) => {
      void supabase.from('team_thread_members').update({ muted_until: muted ? '2999-12-31T00:00:00Z' : null })
        .eq('thread_id', teamId).eq('user_id', userId).then(({ error }) => { if (error) toast('That did not save. Try again.'); else void reload(); });
    },
    setTeamArchived: (teamId, archived) => {
      void supabase.from('team_thread_members').update({ archived_at: archived ? new Date().toISOString() : null })
        .eq('thread_id', teamId).eq('user_id', userId).then(({ error }) => { if (error) toast('That did not save. Try again.'); else void reload(); });
    },
    typing: {},
    keepArchived,
    setKeepArchived,
    markRead,
    setRead,
    setArchived: (ids, archived) => {
      // Unarchiving a closed chat reopens it: a closed chat is always in Archived (Gary, 18 Oct).
      if (!archived) {
        const closed = ids.filter((id) => snap?.conversations.find((c) => c.id === id)?.status === 'closed');
        void Promise.all(closed.map((id) => call({ action: 'reopen', conversation_id: id }))).then(() => saveState(ids, { archived_at: null }));
        return;
      }
      // Archiving also closes the chat (Gary, 19 Oct): archived means nothing more to do, so no reply stays owed and
      // head office is never flagged for a chat nobody will see. A new message from the customer starts a fresh chat.
      const open = ids.filter((id) => { const s = snap?.conversations.find((c) => c.id === id)?.status; return !!s && s !== 'closed'; });
      void Promise.all(open.map((id) => call({ action: 'close', conversation_id: id, outcome: 'closed', reason: 'Archived' })))
        .then(() => saveState(ids, { archived_at: new Date().toISOString(), pinned_at: null }));
    },
    setPinned: (ids, pinned) => void saveState(ids, { pinned_at: pinned ? new Date().toISOString() : null }),
    setMuted: (ids, muted) => void saveState(ids, { muted_until: muted ? '2999-12-31T00:00:00Z' : null }),
    send: (chatId: string, message: OutgoingMessage) => {
      // Live today: typed replies and team notes. Voice notes, photos, approved messages
      // and forwarding follow once their sending side is built.
      // Forwarded text goes as a normal reply; forwarding photos and files is not built yet.
      if (message.body.kind !== 'text' || message.approved || message.button) {
        toast(SOON);
        return;
      }
      if (sending) return;
      setSending(true);
      // A note says who its @mentions reached (Gary, 19 Oct): they get a phone alert; someone who cannot open this
      // chat is named back.
      if (message.note) {
        void supabase.functions.invoke('messaging-send', { body: { action: 'note', conversation_id: chatId, text: message.body.text } })
          .then(async ({ data, error }) => {
            if (error) { toast(await errorText(error)); return; }
            reloadSoon();
            const r = (data ?? {}) as { mentioned?: string[]; not_reached?: string[]; unknown?: string[] };
            const told = r.mentioned?.length ? `${r.mentioned.join(' and ')} alerted.` : '';
            const missed = r.not_reached?.length ? ` ${r.not_reached.join(' and ')} can't open this chat, so not alerted.` : '';
            const nobody = r.unknown?.length ? ` No one called ${r.unknown.map((n) => `@${n}`).join(' or ')}, so nobody was alerted for that.` : '';
            if (told || missed || nobody) toast(`${told}${missed}${nobody}`.trim());
          })
          .finally(() => setSending(false));
        return;
      }
      void call(message.note
        ? { action: 'note', conversation_id: chatId, text: message.body.text }
        : { action: 'send', conversation_id: chatId, text: message.body.text, reply_to_message_id: message.replyTo?.id, sign_with_name: message.signWithName === true, busy: message.busy === true, link_url: message.link?.url, link_label: message.link?.label })
        .finally(() => setSending(false));
    },
    sendFile: (chatId: string, file: File, caption?: string) => {
      if (sending) return;
      setSending(true);
      const form = new FormData();
      form.append('action', 'send_media');
      form.append('conversation_id', chatId);
      form.append('file', file, file.name || 'file');
      if (caption) form.append('caption', caption);
      void call(form).finally(() => setSending(false));
    },
    sendTeam: (teamId, text, forwarded, replyToId, asLeo, live) => call({ action: 'team_send', thread_id: teamId, text, forwarded: forwarded === true, reply_to_message_id: replyToId, as_leo: asLeo === true, live: live === true }),
    // Your own Team message, within 15 minutes; everyone in the chat sees the change.
    teamEdit: (_teamId, messageId, text) => { void call({ action: 'team_edit', message_id: messageId, text }); },
    teamDelete: (_teamId, messageId) => { void call({ action: 'team_delete', message_id: messageId }); },
    sendTeamFile: (teamId, file, caption, live) => {
      if (sending) return;
      setSending(true);
      const form = new FormData();
      form.append('action', 'team_send_media');
      form.append('thread_id', teamId);
      form.append('file', file, file.name || 'file');
      if (caption) form.append('caption', caption);
      if (live) form.append('live', 'true');
      void call(form).finally(() => setSending(false));
    },
    forwardMedia: (sourceKind, messageId, targetKind, targetId) =>
      call({ action: 'forward_media', source_kind: sourceKind, source_message_id: messageId, target_kind: targetKind, target_id: targetId }),
    // Team reactions are seen by both people; star and delete for me are your own.
    teamReact: (teamId, messageId, emoji) => {
      void (emoji
        ? supabase.from('team_message_reactions').upsert({ message_id: messageId, user_id: userId, thread_id: teamId, emoji }, { onConflict: 'message_id,user_id' })
        : supabase.from('team_message_reactions').delete().eq('message_id', messageId).eq('user_id', userId)
      ).then(({ error }) => { if (error) toast('That did not save. Try again.'); else void reload(); });
    },
    teamStar: (teamId, messageId) => {
      const on = !teamData.team.find((t) => t.id === teamId)?.thread.some((m) => m.id === messageId && m.from !== 'day' && m.from !== 'lead' && m.starred);
      void supabase.from('team_message_marks').upsert({ user_id: userId, message_id: messageId, thread_id: teamId, starred: on, updated_at: new Date().toISOString() }, { onConflict: 'user_id,message_id' })
        .then(({ error }) => { if (error) toast('That did not save. Try again.'); else { toast(on ? 'Starred' : 'Unstarred'); void reload(); } });
    },
    teamHide: (teamId, messageId) => {
      void supabase.from('team_message_marks').upsert({ user_id: userId, message_id: messageId, thread_id: teamId, hidden: true, updated_at: new Date().toISOString() }, { onConflict: 'user_id,message_id' })
        .then(({ error }) => { if (error) toast('That did not save. Try again.'); else void reload(); });
    },
    // Our emoji on a customer's message goes to their WhatsApp (messaging-send react).
    react: (chatId, messageId, emoji) => { void call({ action: 'react', conversation_id: chatId, message_id: messageId, emoji: emoji ?? '' }); },
    // Star and Delete for me are this person's own marks, never seen by anyone else.
    toggleStar: (chatId, messageId) => {
      const on = !snap?.marks.get(messageId)?.starred;
      void saveMark(chatId, messageId, { starred: on }).then((ok) => { if (ok) toast(on ? 'Starred' : 'Unstarred'); });
    },
    deleteForMe: (chatId, messageId) => { void saveMark(chatId, messageId, { hidden: true }); },
    takeOver: (chatId) => { void call({ action: 'take_over', conversation_id: chatId }).then((ok) => { if (ok) toast('You have this chat now'); }); },
    setLeoOff: (chatId, off) => {
      void call({ action: 'leo_customer', conversation_id: chatId, leo_off: off })
        .then((ok) => { if (ok) toast(off ? 'Leo is off for this customer' : 'Leo is back on for this customer'); });
    },
    drcAction: (chatId, action) => {
      void call({ action, conversation_id: chatId }).then((ok) => { if (ok) toast(action === 'return_to_ai' ? 'Leo has the chat again' : 'Leo is off for this chat'); });
    },
    tellLeo: (chatId, text, replyNow) => {
      void call({ action: 'tell_leo', conversation_id: chatId, text, reply_now: replyNow })
        .then((ok) => { if (ok) toast(replyNow ? 'Told Leo. He is answering again now' : 'Told Leo. He follows it from his next answer'); });
    },
    reassign: (chatId, to) => { void call({ action: 'reassign', conversation_id: chatId, user_id: to.id }).then((ok) => { if (ok) toast(`Reassigned to ${to.name}`); }); },
    closeChat: (chatId, outcome) => { void call({ action: 'close', conversation_id: chatId, outcome }).then((ok) => { if (ok) toast(outcome === 'booked' ? 'Marked booked' : 'Chat closed'); }); },
    startChat: () => { toast(SOON); return ''; },
    labels: snap?.labels ?? [],
    setLabel: (chatId, labelId, on) => {
      void (on
        ? supabase.from('conversation_labels').insert({ conversation_id: chatId, label_id: labelId, added_by: userId })
        : supabase.from('conversation_labels').delete().eq('conversation_id', chatId).eq('label_id', labelId)
      ).then(({ error }) => { if (error) toast('That label did not save. Try again.'); else void reload(); });
    },
  };

  if (!snap) {
    return (
      <div className="boot">
        <div className="boot-spin" />
      </div>
    );
  }
  return <StoreContext.Provider value={value}>{children}</StoreContext.Provider>;
}

/** A system line about Leo or written by him (his notes, hand-overs, steps back): shown in Leo's colour. */
export function isLeoNote(body: string | null): boolean {
  return /\bLeo\b/.test(body ?? '') || /^Customer's note for the visit/.test(body ?? '');
}
